import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import { MATERIAL_IMPORTS } from '../../shared/material';
import { AdminMapperService } from '../../core/services/admin-mapper.service';
import { ShowsService } from '../../core/services/shows.service';
import { ReservationCancelModalComponent } from '../reservation-cancel-modal/reservation-cancel-modal.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../../core/services/auth.service';
import { RouterLink } from '@angular/router';
import {
  ShowDetail,
  ReservationItem,
  RebookShowOption
} from '../../core/models/show.model';

import { ReservationDetailModalComponent } from '../reservation-detail-modal/reservation-detail-modal.component';
import {
  ReservationRebookDialogResult,
  ReservationRebookModalComponent
} from '../reservation-rebook-modal/reservation-rebook-modal.component';

type ReservationTab = 'Alle' | 'Bestätigt' | 'Storniert' | 'Umgebucht';
type SourceFilter = 'Alle Quellen' | 'Web' | 'Kasse' | 'Admin';
type PlaceTypeFilter = 'Alle Platztypen' | 'Sitzplatz' | 'Stehtisch';
type SortColumn = 'guest' | 'persons' | 'table' | 'block' | 'amount' | 'status' | 'source' | 'placeType';
type SortDirection = 'asc' | 'desc';

interface BlockFilterOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-show-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ...MATERIAL_IMPORTS],
  templateUrl: './show-detail.component.html',
  styleUrl: './show-detail.component.scss'
})
export class ShowDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly showsService = inject(ShowsService);
  private readonly adminMapper = inject(AdminMapperService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly authService = inject(AuthService);

  showDetail?: ShowDetail;
  rebookShowOptions: RebookShowOption[] = [];

  pageTitle = '—';
  pageSubtitle = '—';

  isSubmittingAction = false;

  activeTab: ReservationTab = 'Alle';
  isBlockDropdownOpen = false;
  selectedBlockFilter = 'Alle Zeitblöcke';
  isPageLoading = true;

  searchFilter = '';
  selectedSourceFilter: SourceFilter = 'Alle Quellen';
  selectedPlaceTypeFilter: PlaceTypeFilter = 'Alle Platztypen';

  sortColumn: SortColumn | null = null;
  sortDirection: SortDirection = 'asc';

  readonly sourceFilterOptions: readonly SourceFilter[] = ['Alle Quellen', 'Web', 'Kasse', 'Admin'];
  readonly placeTypeFilterOptions: readonly PlaceTypeFilter[] = ['Alle Platztypen', 'Sitzplatz', 'Stehtisch'];

  private readonly initialAdminRange = this.showsService.getInitialAdminRange();
  private readonly adminStartUtc = this.initialAdminRange.startUtc;
  private readonly adminEndUtc = this.initialAdminRange.endUtc;

  ngOnInit(): void {
    const showId = Number(this.route.snapshot.paramMap.get('id'));
    this.loadPageData(showId);
  }

  @HostListener('document:click')
  closeDropdown(): void {
    this.isBlockDropdownOpen = false;
  }

  goBack(): void {
    this.router.navigate(['/showsList']);
  }

  logout(): void {
    this.authService.logout();
  }

  toggleBlockDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isBlockDropdownOpen = !this.isBlockDropdownOpen;
  }

  selectTab(tab: ReservationTab): void {
    this.activeTab = tab;
  }

  selectBlockFilter(option: string): void {
    this.selectedBlockFilter = option;
    this.isBlockDropdownOpen = false;
  }

  clearReservationFilters(): void {
    this.searchFilter = '';
    this.selectedSourceFilter = 'Alle Quellen';
    this.selectedPlaceTypeFilter = 'Alle Platztypen';
  }

  toggleSort(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      return;
    }

    this.sortColumn = column;
    this.sortDirection = 'asc';
  }

  getSortIcon(column: SortColumn): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }

    return this.sortDirection === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  getPlaceTypeLabel(reservation: ReservationItem): 'Sitzplatz' | 'Stehtisch' | '—' {
    if (reservation.targetPlaceType === 0) {
      return 'Sitzplatz';
    }

    if (reservation.targetPlaceType === 1) {
      return 'Stehtisch';
    }

    return '—';
  }

  get hasActiveReservationFilters(): boolean {
    return (
      !!this.searchFilter.trim() ||
      this.selectedSourceFilter !== 'Alle Quellen' ||
      this.selectedPlaceTypeFilter !== 'Alle Platztypen'
    );
  }

  get blockFilterOptions(): BlockFilterOption[] {
    if (!this.showDetail) {
      return [{ label: 'Alle Zeitblöcke', value: 'Alle Zeitblöcke' }];
    }

    const uniqueOptions = this.showDetail.reservations
      .map(reservation => ({
        label: `${reservation.block} (${reservation.blockTime})`,
        value: `${reservation.block}|${reservation.blockTime}`
      }))
      .filter(
        (option, index, array) =>
          array.findIndex(item => item.value === option.value) === index
      );

    return [
      { label: 'Alle Zeitblöcke', value: 'Alle Zeitblöcke' },
      ...uniqueOptions
    ];
  }

  get filteredReservations(): ReservationItem[] {
    if (!this.showDetail) {
      return [];
    }

    let result = [...this.showDetail.reservations];

    if (this.activeTab === 'Storniert') {
      result = result.filter(
        reservation =>
          reservation.status === 'Storniert' ||
          reservation.status === 'Benutzerstorno'
      );
    } else if (this.activeTab !== 'Alle') {
      result = result.filter(reservation => reservation.status === this.activeTab);
    }

    if (this.selectedBlockFilter !== 'Alle Zeitblöcke') {
      result = result.filter(reservation => {
        const value = `${reservation.block}|${reservation.blockTime}`;
        return value === this.selectedBlockFilter;
      });
    }

    const normalizedSearch = this.searchFilter.trim().toLowerCase();

    if (normalizedSearch) {
      result = result.filter(reservation =>
        reservation.guestName.toLowerCase().includes(normalizedSearch) ||
        reservation.realGuestName.toLowerCase().includes(normalizedSearch) ||
        reservation.table.toLowerCase().includes(normalizedSearch)
      );
    }

    if (this.selectedSourceFilter !== 'Alle Quellen') {
      result = result.filter(reservation => reservation.source === this.selectedSourceFilter);
    }

    if (this.selectedPlaceTypeFilter !== 'Alle Platztypen') {
      result = result.filter(reservation =>
        this.getPlaceTypeLabel(reservation) === this.selectedPlaceTypeFilter
      );
    }

    if (!this.sortColumn) {
      return result;
    }

    return result.sort((a, b) => {
      const compareValue = this.compareReservations(a, b, this.sortColumn as SortColumn);
      return this.sortDirection === 'asc' ? compareValue : -compareValue;
    });
  }

  get emptyStateText(): string {
    if (this.activeTab === 'Bestätigt') {
      return 'Keine bestätigten Reservierungen gefunden.';
    }

    if (this.activeTab === 'Storniert') {
      return 'Keine stornierten Reservierungen gefunden.';
    }

    if (this.activeTab === 'Umgebucht') {
      return 'Keine umgebuchten Reservierungen gefunden.';
    }

    if (this.selectedBlockFilter !== 'Alle Zeitblöcke') {
      return 'Keine Reservierungen für den ausgewählten Zeitblock gefunden.';
    }

    return 'Keine Reservierungen gefunden.';
  }

  get selectedBlockFilterLabel(): string {
    if (this.selectedBlockFilter === 'Alle Zeitblöcke') {
      return 'Alle Zeitblöcke';
    }

    const selectedOption = this.blockFilterOptions.find(
      option => option.value === this.selectedBlockFilter
    );

    return selectedOption?.label ?? 'Alle Zeitblöcke';
  }

  trackByReservationId(_: number, item: { id: number }): number {
    return item.id;
  }

  openReservationDetails(reservation: ReservationItem): void {
    if (this.isSubmittingAction) {
      return;
    }

    this.dialog.open(ReservationDetailModalComponent, {
      data: reservation,
      width: '402px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'reservation-detail-dialog',
      backdropClass: 'reservation-detail-backdrop',
      autoFocus: false,
      restoreFocus: false
    });
  }

  openRebookModal(reservation: ReservationItem): void {
    if (this.isSubmittingAction) {
      return;
    }

    const dialogRef = this.dialog.open(ReservationRebookModalComponent, {
      data: {
        reservation,
        showOptions: this.rebookShowOptions
      },
      width: '568px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'reservation-rebook-dialog',
      backdropClass: 'reservation-rebook-backdrop',
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((result: ReservationRebookDialogResult | undefined) => {
      if (!result) {
        return;
      }

      this.isSubmittingAction = true;

      this.showsService.rebookReservation({
        ProcessID: result.processId,
        ShowID: result.showId,
        AreaID: result.areaId,
        Slot: result.slot,
        ReservedCount: result.reservedCount,
        TargetPlaceType: result.targetPlaceType,
        TargetTableNumber: result.targetTableNumber
      }).subscribe({
        next: () => {
          this.isSubmittingAction = false;

          if (!this.showDetail) {
            this.router.navigate(['/showsList']);
            return;
          }

          this.loadShowDetail(this.showDetail.id);
        },
        error: error => {
          this.isSubmittingAction = false;
          console.error('Failed to rebook reservation.', error);
          alert('Umbuchung fehlgeschlagen.');
        }
      });
    });
  }

  cancelReservation(reservation: ReservationItem): void {
    if (this.isSubmittingAction || reservation.source === 'Kasse') {
      return;
    }

    const dialogRef = this.dialog.open(ReservationCancelModalComponent, {
      data: {
        reservation
      },
      width: '444px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'reservation-cancel-dialog',
      backdropClass: 'reservation-cancel-backdrop',
      autoFocus: false,
      restoreFocus: false
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean | undefined) => {
      if (!confirmed) {
        return;
      }

      this.isSubmittingAction = true;

      this.showsService.cancelReservation(
        reservation.processId,
        reservation.showId,
        this.adminStartUtc,
        this.adminEndUtc,
        'Cancelled by admin'
      ).subscribe({
        next: updatedShowDetail => {
          this.isSubmittingAction = false;

          this.showCancelSuccessSnackbar(reservation);

          if (!updatedShowDetail) {
            this.router.navigate(['/showsList']);
            return;
          }

          this.loadShowDetail(updatedShowDetail.id);
        },
        error: error => {
          this.isSubmittingAction = false;
          console.error('Failed to cancel reservation.', error);
          alert('Stornierung fehlgeschlagen.');
        }
      });
    });
  }

  private compareReservations(a: ReservationItem, b: ReservationItem, column: SortColumn): number {
    switch (column) {
      case 'guest':
        return a.guestName.localeCompare(b.guestName, 'de');
      case 'persons':
        return a.persons - b.persons;
      case 'table':
        return this.getTableNumber(a.table) - this.getTableNumber(b.table);
      case 'block':
        return `${a.block} ${a.blockTime}`.localeCompare(`${b.block} ${b.blockTime}`, 'de');
      case 'amount':
        return this.getAmountNumber(a.amount) - this.getAmountNumber(b.amount);
      case 'status':
        return a.status.localeCompare(b.status, 'de');
      case 'source':
        return a.source.localeCompare(b.source, 'de');
      case 'placeType':
        return this.getPlaceTypeLabel(a).localeCompare(this.getPlaceTypeLabel(b), 'de');
    }
  }

  private getTableNumber(table: string): number {
    const value = table.replace(/\D/g, '');
    return value ? Number(value) : 0;
  }

  private getAmountNumber(amount: string): number {
    return Number(
      amount
        .replace(/[^\d,.-]/g, '')
        .replace('.', '')
        .replace(',', '.')
    ) || 0;
  }

  private loadPageData(showId: number): void {
    this.isPageLoading = true;

    this.showsService.getShowDetailPageData(showId, this.adminStartUtc, this.adminEndUtc).subscribe({
      next: ({ info, detail, rebookOptions }) => {
        this.pageTitle = this.adminMapper.toMusicalName(info);
        this.pageSubtitle = this.adminMapper.toVenueName(info);

        if (!detail) {
          this.isPageLoading = false;
          this.router.navigate(['/showsList']);
          return;
        }

        this.showDetail = detail;
        this.rebookShowOptions = rebookOptions;
        this.isPageLoading = false;
      },
      error: error => {
        console.error('Failed to load show detail page.', error);
        this.isPageLoading = false;
        this.router.navigate(['/showsList']);
      }
    });
  }

  private loadShowDetail(showId: number): void {
    this.isPageLoading = true;

    this.showsService.getShowDetail(showId, this.adminStartUtc, this.adminEndUtc).subscribe({
      next: data => {
        if (!data) {
          this.isPageLoading = false;
          this.router.navigate(['/showsList']);
          return;
        }

        this.showDetail = data;
        this.isPageLoading = false;
      },
      error: error => {
        console.error('Failed to reload show detail.', error);
        this.isPageLoading = false;
      }
    });
  }

  private showCancelSuccessSnackbar(reservation: ReservationItem): void {
    this.snackBar.open(
      `${reservation.reservationCode} wurde erfolgreich storniert.`,
      '✕',
      {
        duration: 4500,
        horizontalPosition: 'end',
        verticalPosition: 'bottom',
        panelClass: ['reservation-success-snackbar']
      }
    );
  }

  get blocklistReturnUrl(): string {
    return this.router.url;
  }
}